import React from "react";

import {
  ClientRouteName,
  getRoutePath,
  URLFormat,
} from "@every.org/common/src/helpers/clientRoutes";

import { EmailPrivacy } from "src/components/EmailPrivacy";
import { DefinitionTable } from "src/components/Legal/DefinitionTable";
import { TopLink } from "src/components/Legal/legalStyles";
import { Link } from "src/components/Link";
import { ArticlePageContent } from "src/components/layout/ArticlePage";
import { DefaultPageLayout } from "src/components/layout/DefaultPageLayout";
import { HeaderTitlePageSection } from "src/components/layout/PageSection";
import { LinkAppearance } from "src/styles/link";

const LAST_UPDATED_STRING = "5/20/2022";

const PrivacyPolicy: React.FCC = () => {
  return (
    <DefaultPageLayout
      pageTitle={"Privacy Policy"}
      headerContent={
        <HeaderTitlePageSection>
          <h1>Privacy Policy</h1>
        </HeaderTitlePageSection>
      }
    >
      <ArticlePageContent>
        <p>
          Here at Every.org, we respect your privacy. In crafting this policy,
          we’ve made commercially reasonable efforts to comply with applicable
          laws and regulatory standards relating to our Site (which, as you’ll
          see below, means both our website and mobile apps). Our goal is to
          adhere to the principles set forth in this Privacy Policy. We aim for
          transparency in this policy so that you can make clear choices about
          your data. This policy was last updated on {LAST_UPDATED_STRING}, and
          will be updated on an ongoing basis, as necessary. We will be sure to
          let you know when we make material changes with a notification on your
          next visit to the Site.
        </p>
        <DefinitionTable />
        <h2>Collection of Data</h2>
        <div>
          We may collect personally identifiable information from you in a
          variety of ways, including, but not limited to, when you:
          <ul>
            <li>Visit our Site</li>
            <li>Create an account on the Site</li>
            <li>Donate</li>
            <li>Subscribe to a newsletter</li>
            <li>Fill out a form</li>
            <li>
              Contact us by email, traditional mail, chat, or other form of
              correspondence
            </li>
            <li>
              In connection with other activities, services, features or
              resources we make available on the Site, including but not limited
              to commenting and reviews
            </li>
          </ul>
        </div>
        <p>
          You may be asked for, as appropriate, your full name, email address,
          billing address, profile image, telephone number, a unique individual
          username and password, and/or social media handles. You may visit our
          Site anonymously, but functionality may be limited, as some areas of
          the Site can only be accessed while logged into an account.
        </p>
        <p>
          When you first log onto our Site, you will have the opportunity to
          create an account. You do not need an account or to provide any
          information to us if you are just browsing the publicly accessible
          portion of the Site, but if you want to make a Donation, Follow other
          People, comment, and/or view content that other People have chosen to
          restrict, then you must create an account, which entails providing us
          with your first and last name, your email address, and month and year
          of birth. If you Donate, you will also need to provide your payment
          information through one of our secure payment processors. Every.org
          only keeps a record of your transaction—credit card and bank account
          details are held by our third-party payment processor, not by
          Every.org.
        </p>
        <p>
          When you create an account with us, you can do so either directly
          through the Site, by providing us a username and password, or through
          a third-party sign-in, such as Facebook connect. If you choose to use
          any of the third-party sign-up/sign-in processes, you are giving us
          permission to see information contained in your public profile on
          those sites. If you do not want us to have access to such information,
          please sign up with Every.org directly, rather than using a third
          party sign-in.
        </p>
        <p>
          When you interact with our Site, we also collect limited
          non-personally identifying information that the browser you used makes
          available automatically. This information includes the internet
          address of the computer or network you used to access our Site, the
          date, time, and page(s) you visited on our Site, the browser and
          operating system you used, and the referring page (the webpage that
          contained the link to our Site that you clicked on to get there). We
          also use data files to help you navigate on the Site. Please see our
          section on Cookies below to learn more about managing your experience.
        </p>
        <p>
          We also collect various data from public sources, such as social
          media, government databases, and other websites, though we do not
          associate such data with individual users of the Site. However, please
          note that if you sign in with Facebook connect or another third-party
          login provider, we may link any data we receive through that
          connection with your account.
        </p>
        <h2>Use of Data</h2>
        <p>
          We have a legitimate interest in ensuring that our Site operates
          correctly and efficiently. Toward that goal, we use the aggregated
          nonpersonal data and information from all users of our Site to measure
          server performance, analyze user traffic patterns, and improve the
          content of our Site and portions of our Site. We may use this
          anonymized data for research purposes as well, including sharing it
          with academic researchers. We sometimes track the keywords that are
          entered into our search engines or through our Sites to measure
          interest in specific topics and to improve the visitor experience on
          the Site.
        </p>
        <p>
          We have a legitimate interest in communicating with you about your
          account and will use your personal information to send you important
          non-commercial emails, such as administrative notices related to our
          Site, your Donations, and customer service inquiries. For instance,
          you may receive an email receipt for Donations you make on the Site.
          This and other noncommercial information is necessary to maintain our
          records. Please note that even if you opt out of receiving marketing
          messages (see below for details), we will still send you these types
          of administrative messages (unless you delete your account, of
          course).
        </p>
        <p>
          <strong>We do not sell your personal information.</strong>
        </p>
        <p>
          With your consent, as noted above, we will use your e-mail address
          and/or other personal information to provide you with information we
          think you will be interested in, including, but not limited to, news,
          updates, information about nonprofits you may be interested in, and
          updates on People and nonprofits that you follow. Even after you have
          agreed to receive such messages, you can let us know at any time if
          you would prefer not to receive any or all of this information by
          following the unsubscribe instructions in any email you receive from
          us, or by sending an e-mail to{" "}
          <Link
            data-tname="emailUnsubscribe"
            to="mailto:unsubscribe@every.org"
            appearance={LinkAppearance.HYPERLINK}
          >
            unsubscribe@every.org
          </Link>{" "}
          that contains the email address you wish us to unsubscribe from our
          lists. Please note that if you do not supply the email address used to
          subscribe in your request, we will be unable to unsubscribe you.
        </p>
        <p>
          As noted above, even if you opt out of receiving marketing messages,
          we will still send you important non-commercial messages so long as
          you maintain your account. Please see below for how to permanently
          delete your account.
        </p>
        <p>
          In the event that Every.org goes through a business transition, such
          as a merger, acquisition, liquidation or sale of all or a portion of
          its assets, your data will, in most instances, be part of the assets
          transferred. You may be notified of any such business transition via
          email and/or notice on the Site.
        </p>
        <p>
          We have a legitimate interest in complying with our legal obligations.
          Accordingly, we may release personal information if we believe in good
          faith that: the law or legal process requires it; we have received a
          valid administrative request from a law enforcement agency; or such
          release is necessary to protect our rights, property, or safety, or
          that of our respective affiliates, business partners, customers, or
          others.
        </p>
        <p>
          Every.org may also use third parties to process your data when it
          serves a legitimate business purpose (such as technical support,
          customer support, and financial institutions). Our contracts with
          these third-party processors require them to comply with the terms and
          the intent of this privacy policy and make commercially reasonable
          efforts to safeguard your information.
        </p>
        <h2>Donation Process</h2>
        <p>
          You may Donate by providing payment information directly on the
          Every.org site (which is powered by third-party vendors with whom we
          have an agreement). When you input your credit card or bank account
          information on the Site, it goes directly to our vendors for
          processing. We only maintain a record of the transaction, not your
          credit card or bank account details.
        </p>
        <h2>Your Privacy Settings</h2>
        <p>
          When you create an account, you choose whether to make your account
          public or private. This setting can be changed at any time in your
          account settings. If you set your account to private, other People on
          the Site who you have not approved to Follow you will not be able to
          see your Donation activity, including nonprofits you supported.
          However, if you make your account public, other People on the Site
          will be able to see your Donation activity, including nonprofits you
          supported.
        </p>
        <p>
          Finally, please note that when you change your profile to Private,
          Every.org will remove your information from public view expeditiously,
          but we have no control over any third parties that may have reviewed
          or collected your information while it remained public.
        </p>
        <p>
          We may add additional granularity to these data sharing options as we
          grow, but for now this is our best option for allowing you to make an
          informed choice about what information you share. Please contact us at{" "}
          <EmailPrivacy /> if you require assistance with utilizing or adjusting
          your privacy settings on the Site.
        </p>
        <h2>Links</h2>
        <p>
          Our Site may contain links to other sites, including sites for other
          nonprofit entities or other forms of philanthropic endeavor. Those
          sites may have their own privacy policies, or no privacy policies at
          all. Every.org is not responsible for the privacy practices employed
          by other sites and those other sites have not agreed to our privacy
          policy. Every.org has no control over or responsibility for any
          unaffiliated third-party sites or the content contained in them, and
          we provide these links solely for the convenience of our visitors. We
          do not sponsor, endorse, or otherwise recommend such sites or any
          products or services they may offer. In short, once you leave our
          Site, we are no longer responsible for the data collection practices
          of the companies operating those sites. This is not to say we have any
          reason not to trust them, we just can’t vouch for them, as we are sure
          you can understand.
        </p>
        <h2>Cookies</h2>
        <p>
          To help us serve you better, we collect information that identifies
          the computer or browser used to access the Site, as well as the user
          account associated with that computer/browser if you are logged in.
          Cookies (and other similar technologies) are pieces of information
          placed in the settings of your web browser by a website server to
          identify your computer when you return to a website. Whenever a site
          welcomes you and tailors data to your specifications, it is reading a
          cookie left in your browser and identifying information that you may
          have registered during previous visits, such as your viewing
          preferences.
        </p>
        <p>
          One category of cookies placed by our Site consists of cookies that do
          not collect any personal information, and are essential to provision
          of a functional website and/or app. Accordingly, we have a legitimate
          interest in placing these cookies (these are known as “Necessary
          Cookies” and include cookies that drive your navigation on our Site.
          Note that you are not required to accept other types of cookies that
          collect personal information in order to use our Site (such as some of
          the cookies described above in the Collection section). If you prefer
          not to accept certain cookies, you may do so at any time by altering
          your information through the tools in your specific web browser, which
          will allow you to exempt the Site. You may also help manage your
          cookies here:{" "}
          <Link
            data-tname="emailSupport"
            to="http://optout.aboutads.info"
            appearance={LinkAppearance.HYPERLINK}
          >
            http://optout.aboutads.info
          </Link>
          .
        </p>
        <p>
          Also please note that some features of our website may not function
          properly if <i>all</i> cookies (including Necessary Cookies) are
          blocked.
        </p>
        <h2>Analytics</h2>
        <p>
          The Site may use Google Analytics Demographics and Interest Reporting
          and/or other Google Analytics for Display Advertising features,
          including Remarketing, Google Display Network Impression Reporting
          and/or the doubleclick Campaign Manager integration. We are required
          by Google to disclose the following information to you in our Privacy
          Statement in connection with our use of any Google Analytics for
          Display Advertising features on the Digital Properties.
        </p>
        <p>
          You may opt out of Google Analytics for Display Advertising or
          customize Google Display Network ads by using the Google Ads Settings
          page. You may also prevent your data from being collected and used by
          Google Analytics by opting out through the use of the Google Analytics
          Opt-out Browser Add-on. If the Digital Properties make use of the
          Google Analytics for Display Advertising Remarketing feature,
          Every.org will use such Remarketing feature to advertise online and
          third-party vendors, including Google, may show Every.org ads on sites
          across the Internet.
        </p>
        <p>
          The Site may use other third-party services similar to Google
          Analytics that use cookies or other similar tools on the Site. For a
          full list of such vendors, please visit{" "}
          <Link
            data-tname="vendorsLink"
            to={getRoutePath({
              format: URLFormat.RELATIVE,
              name: ClientRouteName.VENDORS,
            })}
          >
            https://every.org
            {getRoutePath({
              format: URLFormat.RELATIVE,
              name: ClientRouteName.VENDORS,
            })}
          </Link>
          .
        </p>
        <p>
          Every.org and third party vendors, including Google, may use
          first-party cookies (such as the Google Analytics cookies) and
          third-party cookies (such as the doubleclick cookie) together to (a)
          inform, optimize and serve ads based on a user’s past visits to the
          Site, and/or (b) report how your ad impressions, other uses of ad
          services, and interactions with these ad impressions and ad services
          are related to visits to the Site.
        </p>
        <p>
          As with all information we collect, the data described above may be
          utilized by us to help recognize and understand user preferences, to
          make improvements to the Site, to choose content to display to you,
          and for other business purposes that will allow us to better serve
          you.
        </p>
        <h2>Do-Not-Track Signal</h2>
        <p>
          Because the do-not track signal does not often reflect the preference
          of our consumers, our website does not respond to it. You may,
          however, elect not to receive marketing messages and/or have certain
          cookies placed on your browser, as discussed above, in the Cookies
          section of this Policy.
        </p>
        <h2>{"Children's Access"}</h2>
        <p>
          Consistent with the {"Children's"} Online Privacy Protection Act of
          1998 and its revisions in 2013, our website is a general audience
          website, intended for adult use only. Every.org does not market to
          children under the age of thirteen, nor does it knowingly collect any
          personally identifiable information from children under the age of
          thirteen. You may only use our Site if you are over the age at which
          you can provide consent to data processing under the laws of your
          country (the “Age of Consent”).
        </p>
        <h2>Site Security</h2>
        <p>
          We take reasonable and appropriate security measures to protect
          unauthorized access, alteration or destruction of data located on and
          collected by our Site. Sensitive and private data exchange between the
          Site and its users happens over a SSL secured communication channel
          and is encrypted and protected with digital signatures. We exercise
          reasonable care to protect your non-public personal information.
          Unfortunately, no data transmission over the Internet can be
          guaranteed to be 100% secure. While we strive to protect your
          non-public personal information, we cannot guarantee the security of
          any information you transmit to us or receive from us while it is in
          transit. Once we receive your personal information, we maintain
          physical, electronic and procedural safeguards to protect it. If a
          data breach occurs, we will notify you and the proper EEA authority
          (if required) within 72 hours (if reasonably feasible). If we are
          unable to notify the proper EEA authority within 72 hours, our notice
          will be accompanied by reasons for the delay.
        </p>
        <h2>Dispute Resolution</h2>
        <p>
          If you have an inquiry or complaint regarding whether Every.org has
          violated its obligations under this Privacy Policy as to you (your
          “Complaint”), you should first contact us at:
        </p>
        <p>
          <EmailPrivacy />{" "}
        </p>
        <p>
          We will acknowledge your Complaint within 24 hours and respond to your
          complaint within 45 days. We may ask that you provide additional
          information and/or request a one-on-one discussion or conference.
        </p>
        <h2>{"Right of Access & Updating your Information"}</h2>
        <p>
          You are entitled to access your personal information and to have it
          amended, updated or deleted.
        </p>
        <p>
          You may exercise your right to access your information and obtain:
        </p>
        <ol>
          <li>
            a description of the categories of personal information and sources
            of such personal information we have collected within the past year;
          </li>
          <li>
            a description of the business purpose we have collected such
            personal information for;
          </li>
          <li>
            a description of the specific pieces of personal information we have
            collected within the past year; and
          </li>
          <li>
            information on each third-party company to whom we have disclosed,
            within the past year, personal information pertaining to you for our
            direct marketing purposes
          </li>
        </ol>
        <p>
          In order to obtain such information, please email us at:{" "}
          <EmailPrivacy />
        </p>
        <p>
          Please be sure to provide us with your name and the email address
          about which you are requesting information. In order to verify your
          identity, we may require email verification from the email address we
          have in our records for you. You may be required to provide more
          information in order for us to assist you if we require it to verify
          that you are the authorized account holder. We take this step to
          protect your data. Every.org will answer your request within 45 days
          from its receipt provided that the request is complete and accurate.
        </p>
        <p>
          If you would like to update your information you can do so using the
          Site by updating your account details or by contacting us by sending a
          request in writing to:
        </p>
        <p>
          <EmailPrivacy />
        </p>
        <p>
          When making a request to update your information, please make sure to
          provide the name and email address associated with the account. As
          with access requests, the email address you provide must match the
          email address we have on record for you and we may require further
          information to verify your identity. We will answer your request
          within a reasonable business period from its receipt provided that
          this request is complete and accurate.
        </p>
        <p>
          You may, on legitimate grounds, object to the processing of your
          personal information. Such objection may, however, prohibit us from
          providing the requested service.
        </p>
        <h2>Deleting Your Account</h2>
        <p>
          You can delete your account any time by contacting us at{" "}
          <EmailPrivacy />.
        </p>
        <p>
          To delete your account, you must provide the name under which the
          account was opened as well as the email address you used to create the
          account (names are similar, so we must have your email address to
          permanently delete your account). In order to verify your identity, we
          may require email verification from the email address we have in our
          records for you.
        </p>
        <p>
          We retain personal information as long as we have an ongoing
          relationship with you (in particular, if you have an account with us)
          or as needed to provide services to you. If you delete your account,
          we will retain your data only for as long as needed in order to comply
          with our legal obligations (such as certain records for tax purposes).
        </p>
        <h2>Contact Us</h2>
        <p>
          If you have any questions regarding this privacy statement, please
          contact us at:
        </p>
        <p>
          <EmailPrivacy />
        </p>
        <h2>Policy Changes</h2>
        <p>
          We reserve the right, at our sole discretion, to change, modify, add,
          or remove any portion of this Privacy Policy, in whole or in part, at
          any time. The most up-to-date version of this policy will be posted at{" "}
          <Link
            data-tname="privacyPolicyLink"
            to={getRoutePath({
              format: URLFormat.RELATIVE,
              name: ClientRouteName.PRIVACY_POLICY,
            })}
          >
            https://every.org
            {getRoutePath({
              format: URLFormat.RELATIVE,
              name: ClientRouteName.PRIVACY_POLICY,
            })}
          </Link>
          . By using the Site you agree to be bound by any such revisions.
        </p>
        <TopLink data-tname="privacyPolicy--top" />
      </ArticlePageContent>
    </DefaultPageLayout>
  );
};

// Use default export to support React Lazy Loading.
export default PrivacyPolicy;
